import { useCallback } from 'react';

import {
	type AnalyticsEventAttributes,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';

import { teamCreationExp } from '../components/analytics/experiences';

/**
 * This hook is used to trigger analytics for the team create dialog.
 * It is used to handle the ufo experience for the team creation experience.
 */
export const useTeamCreateDialogAnalytics = () => {
	const { fireEvent } = useAnalyticsEvents();

	const triggerAnalyticsForCreateTeamSuccess = useCallback(
		(attributes: AnalyticsEventAttributes['track.teamCreateDialog.succeeded']) => {
			// @ts-expect-error - operational.teamCreateDialog.succeeded can not be defined in the analytics spec as it is a duplicate event
			fireEvent('operational.teamCreateDialog.succeeded', attributes);
			teamCreationExp.success({
				metadata: {
					...attributes,
					defaultTeamType: attributes.defaultTeamType ?? undefined,
					teamTypeChoiceEnabled:
						attributes.teamTypeChoiceEnabled === null
							? undefined
							: attributes.teamTypeChoiceEnabled,
				},
			});
			fireEvent('track.teamCreateDialog.succeeded', attributes);
		},
		[fireEvent],
	);

	const triggerAnalyticsForCreateTeamFailed = useCallback(
		(attributes: AnalyticsEventAttributes['track.teamCreateDialog.failed']) => {
			// @ts-expect-error - operational.teamCreateDialog.failed can not be defined in the analytics spec as it is a duplicate event
			fireEvent('operational.teamCreateDialog.failed', attributes);
			teamCreationExp.failure({
				metadata: {
					...attributes,
					errorStack: attributes.errorStack ?? undefined,
					traceId: attributes.traceId ?? undefined,
				},
			});
			fireEvent('track.teamCreateDialog.failed', attributes);
		},
		[fireEvent],
	);

	const triggerAnalyticsForSubmitTeamCreateDialog = useCallback(
		(attributes: AnalyticsEventAttributes['ui.teamCreateDialog.submitted']) => {
			fireEvent('ui.teamCreateDialog.submitted', attributes);
			teamCreationExp.start();
		},
		[fireEvent],
	);

	return {
		triggerAnalyticsForCreateTeamSuccess,
		triggerAnalyticsForCreateTeamFailed,
		triggerAnalyticsForSubmitTeamCreateDialog,
	};
};

export default useTeamCreateDialogAnalytics;
